import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import type { InsertOneResult, UpdateResult } from 'mongodb';

const afterCreateCallbacks = new Map<string, (newEntry: WhitelistEntry, options: InsertOneResult) => Promise<void>>();

const afterUpdateCallbacks = new Map<
  string,
  (oldEntry: PartialEntityData<WhitelistEntry>, newEntry: WhitelistEntry, options: UpdateResult) => Promise<void>
>();

export const registerWhitelistAfterUpdateCallback = (
  name: string,
  callback: (
    oldEntry: PartialEntityData<WhitelistEntry>,
    newEntry: WhitelistEntry,
    options: UpdateResult,
  ) => Promise<void>,
) => {
  if (afterUpdateCallbacks.has(name)) {
    throw new Error(`Callback with name ${name} already registered!`);
  }
  afterUpdateCallbacks.set(name, callback);
};

export const registerWhitelistAfterCreateCallback = (
  name: string,
  callback: (newEntry: WhitelistEntry, options: InsertOneResult) => Promise<void>,
) => {
  if (afterCreateCallbacks.has(name)) {
    throw new Error(`Callback with name ${name} already registered!`);
  }
  afterCreateCallbacks.set(name, callback);
};

@Entity()
export class WhitelistEntry extends MongoEntity {
  static override _class = 'WhitelistEntry';
  static override _collectionName = 'whitelist-entries';

  @validate(nonOptional())
  @Field(() => String)
  ticketID: Maybe<string>;

  @Field(() => String)
  note: Maybe<string>;

  static override async afterUpdate(
    oldEntry: PartialEntityData<WhitelistEntry>,
    newEntry: WhitelistEntry,
    options: UpdateResult,
  ) {
    await Promise.all(Array.from(afterUpdateCallbacks.values()).map(callback => callback(oldEntry, newEntry, options)));

    return super.afterUpdate(oldEntry, newEntry, options);
  }

  static override async afterCreate(newEntry: WhitelistEntry, options: InsertOneResult) {
    await Promise.all(Array.from(afterCreateCallbacks.values()).map(callback => callback(newEntry, options)));

    return super.afterCreate(newEntry, options);
  }
}
